import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { cancelSubscription } from '../../services/omiseService';
import { 
  CreditCard, 
  Calendar, 
  Package, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  Clock,
  ArrowRight,
  Loader2,
  Database,
  TrendingUp
} from 'lucide-react';
import SEO from '../../components/SEO';

interface OmiseSubscription {
  id: string;
  plan_id: string;
  current_plan: string;
  billing_cycle: 'monthly' | 'yearly';
  status: 'active' | 'past_due' | 'cancelled' | 'expired';
  period_start: Date;
  period_end: Date;
  amount: number;
  addon_list: any[];
  cancelled_at?: Date;
}

interface OmiseCharge {
  id: string;
  amount: number;
  status: 'pending' | 'successful' | 'failed';
  description: string;
  created_at: Date;
  paid_at?: Date;
}

export default function BillingDashboard() {
  const { user } = useAuthContext();
  const navigate = useNavigate();

  const [subscription, setSubscription] = useState<OmiseSubscription | null>(null);
  const [charges, setCharges] = useState<OmiseCharge[]>([]);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    loadBillingData();
  }, [user, navigate]);

  const loadBillingData = async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Try to get Omise subscription first
      const subscriptionQuery = query(
        collection(db, 'omise_subscriptions'),
        where('userId', '==', user.uid),
        where('status', 'in', ['active', 'past_due', 'cancelled']),
        orderBy('created_at', 'desc'),
        limit(1)
      );

      const subscriptionSnap = await getDocs(subscriptionQuery);
      if (!subscriptionSnap.empty) {
        const doc = subscriptionSnap.docs[0];
        const data = doc.data();
        setSubscription({
          id: doc.id,
          plan_id: data.plan_id,
          current_plan: data.current_plan,
          billing_cycle: data.billing_cycle,
          status: data.status,
          period_start: data.period_start.toDate(),
          period_end: data.period_end.toDate(),
          amount: data.amount,
          addon_list: data.addon_list || [],
          cancelled_at: data.cancelled_at?.toDate(),
        });
      } else {
        // If no Omise subscription, check user's subscription from users collection
        const { doc: firestoreDoc, getDoc } = await import('firebase/firestore');
        const userDoc = await getDoc(firestoreDoc(db, 'users', user.uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.subscriptionTier && userData.subscriptionTier !== 'free') {
            // Create a subscription object from user data
            const billing_cycle = userData.subscriptionTier.includes('yearly') ? 'yearly' : 'monthly';
            setSubscription({
              id: 'user-subscription',
              plan_id: userData.subscriptionTier,
              current_plan: userData.subscriptionTier,
              billing_cycle,
              status: userData.subscriptionStatus || 'active',
              period_start: userData.subscriptionStartDate?.toDate() || new Date(),
              period_end: userData.subscriptionEndDate?.toDate() || new Date(),
              amount: 0, // Free activation
              addon_list: [],
            });
          }
        }
      }

      const chargesQuery = query(
        collection(db, 'omise_charges'),
        where('userId', '==', user.uid),
        orderBy('created_at', 'desc'),
        limit(10)
      );

      const chargesSnap = await getDocs(chargesQuery);
      const chargesData = chargesSnap.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          amount: data.amount,
          status: data.status,
          description: data.description,
          created_at: data.created_at.toDate(),
          paid_at: data.paid_at?.toDate(),
        };
      });
      setCharges(chargesData);
    } catch (err: any) {
      console.error('Error loading billing data:', err);
      setError('ไม่สามารถโหลดข้อมูลได้');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('คุณแน่ใจหรือไม่ที่จะยกเลิกการสมัครสมาชิก? คุณจะยังคงใช้งานได้จนถึงสิ้นรอบบิลปัจจุบัน')) {
      return;
    }

    setCancelling(true);
    setError('');

    try {
      const result = await cancelSubscription();
      alert(result.message);
      await loadBillingData();
    } catch (err: any) {
      console.error('Error cancelling subscription:', err);
      setError(err.message || 'ไม่สามารถยกเลิกการสมัครได้');
    } finally {
      setCancelling(false);
    }
  };

  const getPlanName = (planId: string) => {
    const names: Record<string, string> = {
      pro_monthly: 'Pro (รายเดือน)',
      pro_yearly: 'Pro (รายปี)',
      pro_plus_monthly: 'Pro+ (รายเดือน)',
      pro_plus_yearly: 'Pro+ (รายปี)',
    };
    return names[planId] || planId;
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { icon: any; text: string; className: string }> = {
      active: {
        icon: CheckCircle,
        text: 'ใช้งานอยู่',
        className: 'bg-green-100 text-green-800 border-green-200',
      },
      past_due: {
        icon: AlertCircle,
        text: 'ค้างชำระ',
        className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      },
      cancelled: {
        icon: XCircle,
        text: 'ยกเลิกแล้ว',
        className: 'bg-gray-100 text-gray-800 border-gray-200',
      },
      expired: {
        icon: XCircle,
        text: 'หมดอายุ',
        className: 'bg-red-100 text-red-800 border-red-200',
      },
    };

    const badge = badges[status] || badges.expired;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold border ${badge.className}`}>
        <Icon className="w-4 h-4" />
        {badge.text}
      </span>
    );
  };

  const getChargeStatusBadge = (status: string) => {
    const badges: Record<string, { icon: any; text: string; className: string }> = {
      successful: {
        icon: CheckCircle,
        text: 'สำเร็จ',
        className: 'bg-green-100 text-green-800',
      },
      pending: {
        icon: Clock,
        text: 'รอดำเนินการ',
        className: 'bg-yellow-100 text-yellow-800',
      },
      failed: {
        icon: XCircle,
        text: 'ล้มเหลว',
        className: 'bg-red-100 text-red-800',
      },
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-semibold ${badge.className}`}>
        <Icon className="w-3 h-3" />
        {badge.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">กำลังโหลด...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="การเรียกเก็บเงิน - EduMi"
        description="จัดการการสมัครสมาชิกและดูประวัติการชำระเงิน"
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white py-12 px-4">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8">การเรียกเก็บเงิน</h1>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border-2 border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {subscription ? (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">แพ็กเกจปัจจุบัน</h2>
                  {getStatusBadge(subscription.status)}
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Package className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">แพ็กเกจ</p>
                        <p className="text-xl font-bold text-gray-900">{getPlanName(subscription.plan_id)}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Calendar className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">รอบบิล</p>
                        <p className="text-xl font-bold text-gray-900">
                          {subscription.billing_cycle === 'yearly' ? 'รายปี' : 'รายเดือน'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                        <CreditCard className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">ยอดชำระ</p>
                        <p className="text-xl font-bold text-gray-900">฿{subscription.amount.toLocaleString()}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                        <Clock className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">รอบบิลถัดไป</p>
                        <p className="text-xl font-bold text-gray-900">
                          {subscription.period_end.toLocaleDateString('th-TH', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {subscription.addon_list.length > 0 && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
                    <div className="flex items-center gap-2 mb-3">
                      <Database className="w-5 h-5 text-blue-600" />
                      <h3 className="font-bold text-gray-900">Add-ons ที่ใช้งาน</h3>
                    </div>
                    <div className="space-y-2">
                      {subscription.addon_list.map((addon, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="text-gray-700">{addon.addon_type} (+{addon.storage_gb}GB)</span>
                          <span className="font-semibold text-gray-900">฿{addon.price.toLocaleString()}/{addon.billing_cycle === 'yearly' ? 'ปี' : 'เดือน'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {subscription.status === 'active' && !subscription.cancelled_at && (
                  <div className="flex flex-col sm:flex-row gap-4">
                    <button
                      onClick={() => navigate('/checkout/addon?billing=' + subscription.billing_cycle)}
                      className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all flex items-center justify-center gap-2"
                    >
                      <Database className="w-5 h-5" />
                      เพิ่มพื้นที่จัดเก็บ
                    </button>
                    <button
                      onClick={handleCancelSubscription}
                      disabled={cancelling}
                      className="px-6 py-3 bg-white text-red-600 border-2 border-red-600 rounded-xl font-semibold hover:bg-red-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {cancelling ? (
                        <>
                          <Loader2 className="w-5 h-5 animate-spin" />
                          กำลังยกเลิก...
                        </>
                      ) : (
                        <>
                          <XCircle className="w-5 h-5" />
                          ยกเลิกการสมัคร
                        </>
                      )}
                    </button>
                  </div>
                )}

                {subscription.cancelled_at && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-semibold mb-1">การสมัครถูกยกเลิกแล้ว</p>
                        <p>คุณยังคงสามารถใช้งานได้จนถึง {subscription.period_end.toLocaleDateString('th-TH')}</p>
                      </div>
                    </div>
                  </div>
                )}

                {subscription.status === 'past_due' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-red-800">
                        <p className="font-semibold mb-1">การชำระเงินล้มเหลว</p>
                        <p>กรุณาตรวจสอบข้อมูลบัตรของคุณและลองใหม่อีกครั้ง</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-white rounded-2xl shadow-xl p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">ประวัติการชำระเงิน</h2>
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>

                {charges.length === 0 ? (
                  <div className="text-center py-12">
                    <CreditCard className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-600">ยังไม่มีประวัติการชำระเงิน</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {charges.map((charge) => (
                      <div
                        key={charge.id}
                        className="flex items-center justify-between p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                            <CreditCard className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900">{charge.description}</p>
                            <p className="text-sm text-gray-600">
                              {charge.created_at.toLocaleDateString('th-TH', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-gray-900 mb-1">฿{charge.amount.toLocaleString()}</p>
                          {getChargeStatusBadge(charge.status)}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
              <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-3">ยังไม่มีการสมัครสมาชิก</h2>
              <p className="text-gray-600 mb-6">
                เริ่มต้นใช้งาน EduMi ด้วยการเลือกแพ็กเกจที่เหมาะกับคุณ
              </p>
              <button
                onClick={() => navigate('/pricing')}
                className="px-8 py-4 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-xl font-semibold hover:shadow-xl transition-all inline-flex items-center gap-2"
              >
                ดูแพ็กเกจทั้งหมด
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
